import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useLanguage } from '../../contexts/LanguageContext';
import { deduplicateRecurring } from '../../utils/deduplicateRecurring';
import { upcomingQueryStart, isOngoingOrUpcoming, formatEventDateRange } from '../../utils/eventDates';
import './FoodEventsPage.css';

function FoodEventsPage() {
  const { language } = useLanguage();
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    // Query for upcoming food events; the window is widened so multi-day
    // events already underway are included, then filtered client-side.
    const upcomingQuery = query(
      collection(db, 'events'),
      where('type', '==', 'food'),
      where('date', '>=', upcomingQueryStart(startOfToday)),
      orderBy('date', 'asc')
    );

    // Query for past food events
    const pastQuery = query(
      collection(db, 'events'),
      where('type', '==', 'food'),
      where('date', '<', startOfToday),
      orderBy('date', 'desc')
    );

    const unsubscribeUpcoming = onSnapshot(upcomingQuery, (snapshot) => {
      const events = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(event => isOngoingOrUpcoming(event, startOfToday));
      setUpcomingEvents(deduplicateRecurring(events));
      setLoading(false);
    });

    const unsubscribePast = onSnapshot(pastQuery, (snapshot) => {
      const events = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        // A multi-day event that has started but not ended is not "past"
        .filter(event => !isOngoingOrUpcoming(event, startOfToday));
      setPastEvents(events);
    });

    return () => {
      unsubscribeUpcoming();
      unsubscribePast();
    };
  }, []);

  const formatDate = (event) => {
    if (event.date && event.date.toDate) {
      return formatEventDateRange(event.date, event.endDate, 'en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    return 'Date TBD';
  };

  const formatTime = (date) => {
    if (date && date.toDate) {
      return date.toDate().toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return '';
  };

  if (loading) {
    return (
      <div className="food-events-page">
        <h1 className="food-events-title">Food Events</h1>
        <div className="loading-message">Loading events...</div>
      </div>
    );
  }

  return (
    <div className="food-events-page">
      <h1 className="food-events-title">Food Events</h1>
      <p className="food-events-intro">
        Experience our culinary events, pop-up dinners, and food workshops. 
        Our kitchen space hosts everything from intimate cooking classes to large-scale catering events.
      </p>

      {/* Upcoming Events Section */}
      <section className="events-section">
        <h2 className="section-title">UPCOMING</h2>
        
        {upcomingEvents.length === 0 ? (
          <div className="no-events">
            No upcoming food events scheduled at the moment. Check back soon!
          </div>
        ) : (
          <div className="events-grid">
            {upcomingEvents.map(event => (
              <div key={event.id} className="event-card">
                <div className="event-content">
                  {(event.thumbnailUrl || event.imageUrl) && (
                    <img 
                      src={event.thumbnailUrl || event.imageUrl} 
                      alt={(language === 'sv' && event.title_sv) || event.title}
                      className="event-image"
                    />
                  )}
                  <div className="event-details">
                    <h3 className="event-title">{(language === 'sv' && event.title_sv) || event.title}</h3>
                    <div className="event-date">
                      {formatDate(event)} at {formatTime(event.date)}
                    </div>
                    <p className="event-description">{(language === 'sv' && event.description_sv) || event.description}</p>
                    {event.price && (
                      <div className="event-price">
                        Price: {event.price} SEK
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Past Events Section */}
      {pastEvents.length > 0 && (
        <section className="events-section">
          <h2 className="section-title past">PAST EVENTS</h2>
          
          <div className="events-grid">
            {pastEvents.map(event => (
              <div key={event.id} className="event-card past">
                <div className="event-content">
                  {(event.thumbnailUrl || event.imageUrl) && (
                    <img 
                      src={event.thumbnailUrl || event.imageUrl} 
                      alt={(language === 'sv' && event.title_sv) || event.title}
                      className="event-image past"
                    />
                  )}
                  <div className="event-details">
                    <h3 className="event-title past">{(language === 'sv' && event.title_sv) || event.title}</h3>
                    <div className="event-date past">{formatDate(event)}</div>
                    <p className="event-description past">{(language === 'sv' && event.description_sv) || event.description}</p>
                    {event.price && (
                      <div className="event-price past">
                        Price: {event.price} SEK
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default FoodEventsPage;
